import React, { ReactNode, useState } from "react"
import {
  Box, BoxProps, Center, Flex, Image, SimpleGrid, Stack, Text,
} from "@chakra-ui/react"
import SettingsSection from "components/settings/SettingsSection"
import Modal from "components/dialogs/Modal"
import Icon from "components/elements/Icon"
import ArrowIcon from "components/elements/icons/ArrowIcon"
import BoltIcon from "components/elements/icons/BoltIcon"
import PlayIcon from "components/elements/icons/PlayIcon"
import { ZAPIER_INTEGRATION_GUIDE_LINK } from "utilities/externalLinks"
// Official "Zapier Icon - Orange" from https://zapier.com/press, used unmodified
import zapierIcon from "assets/images/integrations/zapier-icon-orange.png"
import googleSheetsLogo from "assets/images/integrations/google-sheets.png"
import gmailLogo from "assets/images/integrations/gmail.png"
import slackLogo from "assets/images/integrations/slack.png"
import salesforceLogo from "assets/images/integrations/salesforce.png"
import mailchimpLogo from "assets/images/integrations/mailchimp.png"
import hubspotLogo from "assets/images/integrations/hubspot.png"
import RoundedList from "../RoundedList"
import LinkListItem from "../LinkListItem"
import ZapierWorkflowElement, { loadZapierElements } from "./ZapierWorkflowElement"

const APPS = [
  { name: "Google Sheets", src: googleSheetsLogo },
  { name: "Gmail", src: gmailLogo },
  { name: "Slack", src: slackLogo },
  { name: "Salesforce", src: salesforceLogo },
  { name: "Mailchimp", src: mailchimpLogo },
  { name: "HubSpot", src: hubspotLogo },
]

const TRIGGERS = [
  "New Guest",
  "New Form Submission",
  "Updated Guest Details",
  "New Guest Check-In",
  "Deleted Guest",
  "Updated Guest Status",
]
const ACTIONS = ["Create Guest", "Update Guest", "Find Guest", "Find or Create Guest"]

const BLUE = { bg: "blue.50", accent: "blue.500" }
const GREEN = { bg: "green.50", accent: "green.600" }

type Palette = typeof BLUE

// Wide enough for the trigger card (full names, one line each) next to Zapier and the apps
const ROW_LAYOUT = "@container (min-width: 700px)"

const FlowCard = ({ children, ...props }: BoxProps) => (
  <Box bg="white" borderWidth={1} borderColor="gray.300" borderRadius="xl" p={3} flexShrink={0} {...props}>
    {children}
  </Box>
)

const DioboxCard = ({ items }: { items: string[] }) => (
  <FlowCard sx={{ [ROW_LAYOUT]: { flex: 1 } }}>
    <Stack spacing={3} align="center">
      <Icon icon="diobox-logo" size={24} h={6} color="gray.800" />
      <SimpleGrid templateColumns="repeat(2, 1fr)" spacing={2} w="full">
        {items.map((item) => (
          <Center key={item} px={3} py={1} borderWidth={1} borderColor="gray.300" borderRadius="full">
            <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap">{item}</Text>
          </Center>
        ))}
      </SimpleGrid>
    </Stack>
  </FlowCard>
)

const ZapierCard = () => (
  <FlowCard p={4}>
    <Image src={zapierIcon} alt="Zapier" boxSize={14} borderRadius="md" />
  </FlowCard>
)

const ARROW_LENGTH = 24
const APP_TILE_SIZE = 10
// Shows the first two rows and the top of a third, suggesting the list goes on
const APPS_GRID_HEIGHT = "104px"

const AppsCard = () => (
  <FlowCard pb={0} overflow="hidden">
    <Stack spacing={2} align="center">
      <Text fontSize="xs" fontWeight="bold" whiteSpace="nowrap">Your Apps + 1000s more</Text>
      <Box h={APPS_GRID_HEIGHT} overflow="hidden">
        <SimpleGrid columns={3} spacing={2}>
          {APPS.map((app) => (
            <Center key={app.name} boxSize={APP_TILE_SIZE} borderWidth={1} borderColor="gray.300" borderRadius="md">
              <Image src={app.src} alt={app.name} boxSize={6} objectFit="contain" />
            </Center>
          ))}
          {[1, 2, 3].map((n) => (
            <Box key={n} boxSize={APP_TILE_SIZE} borderWidth={1} borderColor="gray.300" borderRadius="md" />
          ))}
        </SimpleGrid>
      </Box>
    </Stack>
  </FlowCard>
)

type FlowPanelProps = {
  palette: Palette
  icon: ReactNode
  title: string
  description: string
  steps: ReactNode[]
}

const FlowPanel = ({
  palette, icon, title, description, steps,
}: FlowPanelProps) => (
  <Box bg={palette.bg} p={6} sx={{ containerType: "inline-size" }}>
    <Stack spacing={4}>
      <Flex align="center" gap={3}>
        <Center boxSize={9} borderRadius="full" bg={palette.accent} color="white" flexShrink={0}>{icon}</Center>
        <Text fontSize="xl" fontWeight="semibold">{title}</Text>
      </Flex>
      <Text>{description}</Text>
      <Flex direction="column" align="center" justify="center" gap={1} pt={2} sx={{ [ROW_LAYOUT]: { flexDirection: "row" } }}>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <ArrowIcon
                length={ARROW_LENGTH}
                // Rotated in the vertical layout: keep its full length clear above and below
                my={`${(ARROW_LENGTH - 14) / 2}px`}
                color="gray.400"
                flexShrink={0}
                transform="rotate(90deg)"
                sx={{ [ROW_LAYOUT]: { transform: "none", my: 0 } }}
              />
            )}
            {step}
          </React.Fragment>
        ))}
      </Flex>
    </Stack>
  </Box>
)

type Props = {
  enabled: boolean
}

const HowZapierWorks = ({ enabled }: Props) => {
  const [showBuilder, setShowBuilder] = useState(false)
  // The builder needs Diobox's embed client ID, set per environment. Read at render time so
  // specs can stub it.
  const clientId = import.meta.env.VITE_ZAPIER_CLIENT_ID

  return (
    <SettingsSection
      title="How Diobox Works with Zapier"
      description="A Zapier workflow is called a Zap. Every Zap starts with a trigger and runs one or more actions. Diobox can be used in a Zap in two ways."
    >
      <RoundedList>
        <FlowPanel
          palette={BLUE}
          icon={<BoltIcon boxSize={7} />}
          title="Triggers"
          description="When something happens in Diobox, Zapier can start an automated workflow in your other apps."
          steps={[
            <DioboxCard items={TRIGGERS} />,
            <ZapierCard />,
            <AppsCard />,
          ]}
        />
        <FlowPanel
          palette={GREEN}
          icon={<PlayIcon boxSize={7} />}
          title="Actions"
          description="Use Zapier to create or update guests in Diobox from other apps."
          steps={[
            <AppsCard />,
            <ZapierCard />,
            <DioboxCard items={ACTIONS} />,
          ]}
        />
        <LinkListItem
          title="Diobox + Zapier Integration Guide"
          description="Learn how to set up Zapier with Diobox and sync guest data with thousands of other apps."
          href={ZAPIER_INTEGRATION_GUIDE_LINK}
          isExternal
        />
        {enabled && clientId && (
          // Starts loading Zapier's script on intent, so the modal isn't blank on first open
          <Box onMouseEnter={loadZapierElements} onFocus={loadZapierElements}>
            <LinkListItem
              title="Build a Zap"
              description="Start from a ready-made workflow or build your own, without leaving Diobox."
              onClick={() => setShowBuilder(true)}
            />
          </Box>
        )}
      </RoundedList>
      {clientId && (
        <Modal show={showBuilder} hide={() => setShowBuilder(false)} title="Build a Zap" size="5xl">
          <ZapierWorkflowElement clientId={clientId} />
        </Modal>
      )}
    </SettingsSection>
  )
}

export default HowZapierWorks
